'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useTerminal } from '@/hooks/useTerminal';
import { executeCommand } from '@/lib/commands';
import { WELCOME_BRIEF } from '@/lib/challenge-spec';
import { useTransition } from '@/contexts/TransitionContext';
import { Pong } from './Pong';

export default function Terminal({ onRestart }: { onRestart?: () => void }) {
  const {
    history,
    addLine,
    addLines,
    addCommand,
    clear,
    getPreviousCommand,
    getNextCommand,
  } = useTerminal();
  const { startSwarmTransition } = useTransition();
  const [input, setInput] = useState('');
  const [showPong, setShowPong] = useState(false);
  const [adventureMode, setAdventureMode] = useState(false);
  const [adventureLoading, setAdventureLoading] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [scrambledDisplay, setScrambledDisplay] = useState('');
  const adventureHistory = useRef<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const gameState = useRef<string>('');
  const streamingRef = useRef('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      addLines(WELCOME_BRIEF, 'system');
    }
  }, [addLines]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, scrambledDisplay]);

  useEffect(() => {
    if (!showPong) {
      inputRef.current?.focus();
    }
  }, [history, showPong, adventureMode, adventureLoading]);

  const stripState = (text: string) => text.replace(/\s*<<STATE>>[\s\S]*/, '').trimEnd();

  const GLITCH_CHARS = '<>[]{}|/\\#$%&@~░▒▓█▐─┼';
  const SCRAMBLE_TAIL = 14;

  useEffect(() => {
    streamingRef.current = streamingText;
    if (!streamingText) setScrambledDisplay('');
  }, [streamingText]);

  useEffect(() => {
    if (!adventureLoading) return;
    const interval = setInterval(() => {
      const raw = streamingRef.current;
      if (!raw) return;
      const clean = stripState(raw).split('\n').map((l) => l.trimStart()).join('\n');
      if (clean.length <= SCRAMBLE_TAIL) {
        setScrambledDisplay(clean);
        return;
      }
      const settled = clean.slice(0, -SCRAMBLE_TAIL);
      const tail = clean.slice(-SCRAMBLE_TAIL);
      const scrambled = tail.split('').map((ch, i) => {
        if (ch === ' ' || ch === '\n') return ch;
        const distFromEnd = tail.length - i;
        if (Math.random() < (distFromEnd / SCRAMBLE_TAIL) * 0.4) {
          return GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)];
        }
        return ch;
      }).join('');
      setScrambledDisplay(settled + scrambled);
    }, 150);
    return () => clearInterval(interval);
  }, [adventureLoading]);

  const sendAdventureMessage = useCallback(
    async (message: string) => {
      const userContent = gameState.current
        ? `[CURRENT GAME STATE]\n${gameState.current}\n[/CURRENT GAME STATE]\n\n${message}`
        : message;
      const newHistory = [
        ...adventureHistory.current,
        { role: 'user' as const, content: userContent },
      ];
      adventureHistory.current = newHistory;
      setAdventureLoading(true);
      setStreamingText('');

      try {
        const res = await fetch('/api/adventure', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: newHistory }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error || 'Connection failed');
        }

        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          setStreamingText(buffer);
        }

        const stateMatch = buffer.match(/<<STATE>>([\s\S]*?)<<END_STATE>>/);
        if (stateMatch) {
          gameState.current = stateMatch[1].trim();
        }

        adventureHistory.current = [
          ...adventureHistory.current,
          { role: 'assistant' as const, content: buffer },
        ];

        setStreamingText('');
        let display = stripState(buffer);
        const isGameOver = /game_over=true/i.test(gameState.current);
        const lines = display.split('\n').map((l) => l.trimStart());
        addLines(lines, isGameOver ? 'red' : 'cyan');

        if (isGameOver) {
          setAdventureMode(false);
          adventureHistory.current = [];
          gameState.current = '';
          addLine('', 'output');
          addLine('Connection to NEXUS lost.', 'cyan');
          addLine('', 'output');
        }
      } catch (err) {
        setStreamingText('');
        const msg = err instanceof Error ? err.message : 'Connection lost';
        addLine(`  [NEXUS ERROR] ${msg}`, 'red');
      } finally {
        setAdventureLoading(false);
      }
    },
    [addLine, addLines]
  );

  const handleSubmit = useCallback(async () => {
    const trimmed = input.trim();

    // Adventure mode
    if (adventureMode) {
      if (!trimmed || adventureLoading) {
        setInput('');
        return;
      }
      const cmd = trimmed.toLowerCase();
      if (cmd === 'quit' || cmd === 'exit' || cmd === 'exit adventure') {
        setAdventureMode(false);
        adventureHistory.current = [];
        gameState.current = '';
        addLine(`> ${trimmed}`, 'input');
        addLine('', 'output');
        addLine('Disconnected from NEXUS.', 'cyan');
        addLine('', 'output');
        setInput('');
        inputRef.current?.focus();
        return;
      }
      addLine(`> ${trimmed}`, 'input');
      setInput('');
      await sendAdventureMessage(trimmed);
      inputRef.current?.focus();
      return;
    }

    // Normal mode
    if (!trimmed) {
      addLine('', 'output');
      setInput('');
      return;
    }

    addCommand(trimmed);
    const result = executeCommand(trimmed);

    if (result.special === 'clear') {
      clear();
    } else if (result.special === 'pong') {
      addLines(result.lines, result.type);
      setShowPong(true);
    } else if (result.special === 'adventure') {
      addLines(result.lines, result.type);
      setAdventureMode(true);
      setInput('');
      await sendAdventureMessage('look');
      inputRef.current?.focus();
      return;
    } else if (result.special === 'swarm') {
      addLines(result.lines, result.type);
      startSwarmTransition();
      return;
    } else if (result.special === 'restart') {
      addLines(result.lines, result.type);
      setTimeout(() => onRestart?.(), 500);
      return;
    } else {
      addLines(result.lines, result.type);
    }

    setInput('');
    inputRef.current?.focus();
  }, [
    input,
    adventureMode,
    adventureLoading,
    addCommand,
    addLine,
    addLines,
    clear,
    sendAdventureMessage,
    startSwarmTransition,
    onRestart,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSubmit();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setInput(getPreviousCommand());
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setInput(getNextCommand());
      }
    },
    [handleSubmit, getPreviousCommand, getNextCommand]
  );

  if (showPong) {
    return (
      <div className="h-full flex items-center justify-center bg-surface">
        <Pong onExit={() => setShowPong(false)} />
      </div>
    );
  }

  const lineColor = (type: string) => {
    switch (type) {
      case 'input':
        return 'text-secondary';
      case 'error':
      case 'red':
        return 'text-red';
      case 'amber':
        return 'text-amber';
      case 'cyan':
        return 'text-cyan';
      case 'system':
        return 'text-amber/70';
      default:
        return 'text-primary';
    }
  };

  const prompt = adventureMode ? '>' : 'moment >';
  const promptColor = adventureMode ? 'text-secondary' : 'text-amber';
  const caretColor = adventureMode ? 'caret-secondary' : 'caret-amber';

  return (
    <div
      className="flex-1 min-h-0 flex flex-col text-[13px] leading-[1.7] cursor-text"
      onClick={() => inputRef.current?.focus()}
    >
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 pt-3 pb-0">
        {history.map((line) => {
          const isBold = line.content.startsWith('**');
          const content = isBold ? line.content.slice(2) : line.content;
          const isHR = /^[\s]*[─━═]{4,}/.test(line.content);
          return (
            <div
              key={line.id}
              className={`${isHR ? 'whitespace-pre overflow-hidden' : 'whitespace-pre-wrap break-all'} ${lineColor(line.type)} ${line.type === 'cyan' || line.type === 'red' ? 'pl-5' : ''} ${isBold ? 'font-bold text-primary' : ''}`}
            >
              {content || '\u00A0'}
            </div>
          );
        })}
        {streamingText && (
          <div className="whitespace-pre-wrap break-all text-cyan pl-5">
            {scrambledDisplay}
          </div>
        )}
      </div>

      <div className="shrink-0 flex items-center px-4 py-3 gap-2 border-t border-stroke/50">
        <span
          className={`${promptColor} font-medium text-[13px] shrink-0`}
        >
          {prompt}
        </span>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          className={`flex-1 bg-transparent outline-none ${adventureMode ? 'text-secondary' : 'text-primary'} ${caretColor} text-[13px]`}
          spellCheck={false}
          autoComplete="off"
          autoCapitalize="off"
          disabled={false}
          autoFocus
        />
        {adventureLoading && (
          <span className="text-dim text-[11px] animate-pulse">...</span>
        )}
      </div>
    </div>
  );
}
